import { useState, useEffect, useMemo } from "react"

import { Container, Typography, Box, Divider } from "@mui/material"

import { getTenantDetails } from "../../api/tenant"
import { getSpecificBranchByTenant } from "../../api/booking"

import TenantLayout from "../../layouts/TenantLayout"
import useDeviceType from "../../utils/useDeviceType"
import VenueGallery from "./components/VenueGallery"
import BreadCrumbs from "./components/BreadCrumbs"
import VenueDetails from "./components/VenueDetails"
import VenueFoodPackages from "./components/VenueFoodPackages"
import VenueIncluded from "./components/VenueIncluded"
import BookingCard from "./components/BookingCard"
import PaymentInstructions from "./components/PaymentInstructions"
import BackButton from "../../components/Buttons/BackButton"
import CollapsibleSection from "../../components/shared/CollapsibleSection"
import DOMPurify from "dompurify"

export default function GenericTenantPage() {
  const { isMobile } = useDeviceType()
  const [tenant, setTenantDetails] = useState(null)
  const [selectedBranchId, setSelectedBranchId] = useState(null)
  const [selectedBranchDetails, setSelectedBranchDetails] = useState(null)
  const [roomStats, setRoomStats] = useState({
    startsAtPrice: null,
    capacityRangeLabel: "",
  })

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" })
  }, [])

  useEffect(() => {
    const fetchTenantData = async () => {
      try {
        const tenantData = await getTenantDetails()
        setTenantDetails(tenantData)
        if (tenantData?.data?.id) {
          sessionStorage.setItem("tenantId", tenantData?.data?.id)
        }
        const defaultBranchId = tenantData?.data?.branches?.[0]?.id
        if (defaultBranchId) {
          setSelectedBranchId(Number(defaultBranchId))
        }
      } catch (error) {
        console.error("Error loading tenant page data:", error)
      }
    }

    fetchTenantData()
  }, [])

  useEffect(() => {
    if (!tenant?.data?.id || !selectedBranchId) return
    let cancelled = false

    const fetchSelectedBranch = async () => {
      try {
        const data = await getSpecificBranchByTenant(
          tenant.data.id,
          selectedBranchId,
        )
        if (cancelled) return

        const branch = Array.isArray(data) ? data[0] : null
        setSelectedBranchDetails(branch || null)
      } catch (error) {
        console.error("Error loading selected branch details:", error)
        if (!cancelled) setSelectedBranchDetails(null)
      }
    }

    fetchSelectedBranch()

    return () => {
      cancelled = true
    }
  }, [tenant?.data?.id, selectedBranchId])

  const displayTenant = useMemo(() => {
    if (!tenant) return tenant
    if (!selectedBranchDetails) return tenant

    return {
      ...tenant,
      data: {
        ...tenant.data,
        branches: [selectedBranchDetails],
        gallery_images: selectedBranchDetails.gallery_images || [],
        food_packages: selectedBranchDetails.food_packages || [],
        tags: selectedBranchDetails.event_types || tenant?.data?.tags || [],
      },
    }
  }, [tenant, selectedBranchDetails])

  const activeBranch = displayTenant?.data?.branches?.[0]

  const detailsSx = {
    color: "text.subcaption",
    fontSize: "14px",
    fontWeight: 500,
  }

  return (
    <TenantLayout>
      <Container
        maxWidth="lg"
        disableGutters
        sx={{ px: { xs: 2, sm: 3, md: 0 }, py: isMobile ? 8 : 2 }}
      >
        <>
          {isMobile ? (
            <BackButton to="/home" />
          ) : (
            <BreadCrumbs tenant={displayTenant} />
          )}

          <VenueGallery tenant={displayTenant} />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(12, 1fr)" },
              gap: 2,
              mt: 3,
            }}
          >
            <Box
              sx={{
                gridColumn: { xs: "1 / span 12", md: "span 8" },
                gridRow: "span 8",
              }}
            >
              <VenueDetails
                detailsSx={detailsSx}
                tenant={displayTenant}
                roomStats={roomStats}
              />

              <Typography
                sx={{
                  my: 3,
                  fontWeight: 700,
                  fontSize: "18px",
                  color: "text.heading",
                }}
              >
                About this Venue
              </Typography>
              <Typography
                component="div"
                sx={{
                  ...detailsSx,

                  "& p": {
                    margin: 0,
                  },

                  "& div": {
                    margin: 0,
                  },

                  "& br": {
                    display: "block",
                    content: '""',
                    marginBottom: "8px",
                  },

                  "& ul, & ol": {
                    paddingLeft: "24px",
                    marginTop: "8px",
                    marginBottom: "8px",
                  },

                  "& li": {
                    marginBottom: "4px",
                  },
                }}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(activeBranch?.description || ""),
                }}
              />

              <Divider sx={{ my: 5 }} />

              <VenueFoodPackages detailsSx={detailsSx} tenant={displayTenant} />

              <VenueIncluded tenant={displayTenant} />

              <Box sx={{ mt: 4 }}>
                <Typography
                  sx={{
                    fontSize: "18px",
                    fontWeight: 700,
                    color: "text.heading",
                    mb: 2,
                  }}
                >
                  Location & Directions
                </Typography>

                <Box
                  component="iframe"
                  src={`https://www.google.com/maps?q=${activeBranch?.latitude},${activeBranch?.longitude}&hl=es;z=14&output=embed`}
                  width="100%"
                  height="350px"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </Box>

              <Divider sx={{ my: 2 }} />
              <CollapsibleSection title="Payment Instructions">
                <PaymentInstructions
                  instructionsHtml={activeBranch?.payment_instructions}
                />
              </CollapsibleSection>
            </Box>

            <Box
              sx={{
                gridColumn: { xs: "1 / span 12", md: "9 / span 4" },
                gridRow: "span 8",
              }}
            >
              <BookingCard
                tenant={displayTenant}
                selectedVenueId={selectedBranchId}
                onVenueChange={setSelectedBranchId}
                onRoomStatsChange={setRoomStats}
              />
            </Box>
          </Box>
        </>
      </Container>
    </TenantLayout>
  )
}
